import * as signalR from '@microsoft/signalr';
import { BASE_URL } from '../constants/api';

// SignalR notification payload types
export interface ReservationNotificationPayload {
  type: 'ReservationCreated' | 'ReservationCancelled' | 'ReservationUpdated';
  facilityId: string;
  businessProfileId: string | null;
  reservationId: string;
  date: string; // yyyy-MM-dd format
  timestamp: string;
}

// Event callback types
export type ReservationEventCallback = (payload: ReservationNotificationPayload) => void;

// Connection state
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private stateChangeCallbacks: Set<(state: ConnectionState) => void> = new Set();
  private eventCallbacks: {
    reservationCreated: Set<ReservationEventCallback>;
    reservationCancelled: Set<ReservationEventCallback>;
    reservationUpdated: Set<ReservationEventCallback>;
  } = {
    reservationCreated: new Set(),
    reservationCancelled: new Set(),
    reservationUpdated: new Set(),
  };
  private subscribedFacilities: Set<string> = new Set();
  private subscribedBusinesses: Set<string> = new Set();
  private maxReconnectAttempts = 5;

  /**
   * Get the current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Subscribe to connection state changes
   */
  onStateChange(callback: (state: ConnectionState) => void): () => void {
    this.stateChangeCallbacks.add(callback);
    // Immediately call with current state
    callback(this.connectionState);
    return () => {
      this.stateChangeCallbacks.delete(callback);
    };
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.stateChangeCallbacks.forEach(callback => callback(state));
  }

  /**
   * Connect to the SignalR hub
   */
  async connect(token: string): Promise<void> {
    if (this.connection) {
      // Already connected or connecting
      if (this.connection.state === signalR.HubConnectionState.Connected) {
        return;
      }
      // Disconnect existing connection before creating new one
      await this.disconnect();
    }

    this.setConnectionState('connecting');

    const hubUrl = `${BASE_URL}/hubs/reservations`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 4s, 8s, 16s, then 30s max
          if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
            return null; // Stop retrying
          }
          const delay = Math.min(Math.pow(2, retryContext.previousRetryCount) * 1000, 30000);
          return delay;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Set up event handlers
    this.connection.on('ReservationCreated', (payload: ReservationNotificationPayload) => {
      console.log('[SignalR] ReservationCreated:', payload);
      this.eventCallbacks.reservationCreated.forEach(callback => callback(payload));
    });

    this.connection.on('ReservationCancelled', (payload: ReservationNotificationPayload) => {
      console.log('[SignalR] ReservationCancelled:', payload);
      this.eventCallbacks.reservationCancelled.forEach(callback => callback(payload));
    });

    this.connection.on('ReservationUpdated', (payload: ReservationNotificationPayload) => {
      console.log('[SignalR] ReservationUpdated:', payload);
      this.eventCallbacks.reservationUpdated.forEach(callback => callback(payload));
    });

    // Set up connection state handlers
    this.connection.onreconnecting(() => {
      console.log('[SignalR] Reconnecting...');
      this.setConnectionState('reconnecting');
    });

    this.connection.onreconnected(async () => {
      console.log('[SignalR] Reconnected');
      this.setConnectionState('connected');
      // Re-subscribe to all groups
      await this.resubscribeToGroups();
    });

    this.connection.onclose((error) => {
      console.log('[SignalR] Connection closed', error);
      this.setConnectionState('disconnected');
    });

    try {
      await this.connection.start();
      console.log('[SignalR] Connected successfully');
      this.setConnectionState('connected');
    } catch (error) {
      console.error('[SignalR] Connection failed:', error);
      this.setConnectionState('disconnected');
      throw error;
    }
  }

  /**
   * Disconnect from the SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('[SignalR] Error during disconnect:', error);
      }
      this.connection = null;
    }
    this.subscribedFacilities.clear();
    this.subscribedBusinesses.clear();
    this.setConnectionState('disconnected');
  }

  /**
   * Join a facility group to receive updates for that facility
   */
  async joinFacilityGroup(facilityId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] Cannot join facility group: not connected');
      return;
    }

    try {
      await this.connection.invoke('JoinFacilityGroup', facilityId);
      this.subscribedFacilities.add(facilityId);
      console.log(`[SignalR] Joined facility group: ${facilityId}`);
    } catch (error) {
      console.error(`[SignalR] Failed to join facility group ${facilityId}:`, error);
      throw error;
    }
  }

  /**
   * Leave a facility group
   */
  async leaveFacilityGroup(facilityId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('LeaveFacilityGroup', facilityId);
      this.subscribedFacilities.delete(facilityId);
      console.log(`[SignalR] Left facility group: ${facilityId}`);
    } catch (error) {
      console.error(`[SignalR] Failed to leave facility group ${facilityId}:`, error);
    }
  }

  /**
   * Join a business profile group to receive updates for all facilities in that business
   */
  async joinBusinessGroup(businessProfileId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] Cannot join business group: not connected');
      return;
    }

    try {
      await this.connection.invoke('JoinBusinessGroup', businessProfileId);
      this.subscribedBusinesses.add(businessProfileId);
      console.log(`[SignalR] Joined business group: ${businessProfileId}`);
    } catch (error) {
      console.error(`[SignalR] Failed to join business group ${businessProfileId}:`, error);
      throw error;
    }
  }

  /**
   * Leave a business profile group
   */
  async leaveBusinessGroup(businessProfileId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('LeaveBusinessGroup', businessProfileId);
      this.subscribedBusinesses.delete(businessProfileId);
      console.log(`[SignalR] Left business group: ${businessProfileId}`);
    } catch (error) {
      console.error(`[SignalR] Failed to leave business group ${businessProfileId}:`, error);
    }
  }

  /**
   * Re-subscribe to all groups after reconnection
   */
  private async resubscribeToGroups(): Promise<void> {
    const facilityPromises = Array.from(this.subscribedFacilities).map(id =>
      this.connection?.invoke('JoinFacilityGroup', id).catch(err =>
        console.error(`[SignalR] Failed to resubscribe to facility ${id}:`, err)
      )
    );

    const businessPromises = Array.from(this.subscribedBusinesses).map(id =>
      this.connection?.invoke('JoinBusinessGroup', id).catch(err =>
        console.error(`[SignalR] Failed to resubscribe to business ${id}:`, err)
      )
    );

    await Promise.all([...facilityPromises, ...businessPromises]);
    console.log('[SignalR] Resubscribed to all groups');
  }

  /**
   * Register a callback for reservation created events
   */
  onReservationCreated(callback: ReservationEventCallback): () => void {
    this.eventCallbacks.reservationCreated.add(callback);
    return () => {
      this.eventCallbacks.reservationCreated.delete(callback);
    };
  }

  /**
   * Register a callback for reservation cancelled events
   */
  onReservationCancelled(callback: ReservationEventCallback): () => void {
    this.eventCallbacks.reservationCancelled.add(callback);
    return () => {
      this.eventCallbacks.reservationCancelled.delete(callback);
    };
  }

  /**
   * Register a callback for reservation updated events
   */
  onReservationUpdated(callback: ReservationEventCallback): () => void {
    this.eventCallbacks.reservationUpdated.add(callback);
    return () => {
      this.eventCallbacks.reservationUpdated.delete(callback);
    };
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Export singleton instance
export const signalrService = new SignalRService();
